import {
  EMPTY_SEARCH_DAY_RANGE,
  isDayRangeInvalid,
  type SearchDayRange,
} from './message-search-utils';

interface SearchDateFilterProps {
  value: SearchDayRange;
  onChange: (value: SearchDayRange) => void;
}

const INPUT_CLASS =
  'rounded-md border border-border bg-background px-1.5 py-0.5 text-xs pointer-coarse:text-base text-foreground outline-none focus:ring-2 focus:ring-primary/40';

/**
 * Date bounds for a chat search, as two native date inputs: "After" is the
 * first day included, "Before" the first day excluded (so a message sent ON
 * the "before" day is not found — Slack's convention, and what `before:` in
 * the search box means). Days are the user's local days; the hooks turn them
 * into instants. An "after" later than "before" is shown as invalid rather
 * than silently swapped — the user typed it, the user corrects it.
 */
export function SearchDateFilter({ value, onChange }: SearchDateFilterProps) {
  const invalid = isDayRangeInvalid(value);

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <label className="flex items-center gap-1 text-xs text-muted-foreground">
        <span id="search-after-day">After</span>
        <input
          type="date"
          value={value.after ?? ''}
          max={value.before ?? undefined}
          aria-labelledby="search-after-day"
          aria-label="After day"
          aria-invalid={invalid || undefined}
          onChange={(e) => onChange({ ...value, after: e.target.value || null })}
          className={INPUT_CLASS}
        />
      </label>
      <label className="flex items-center gap-1 text-xs text-muted-foreground">
        <span id="search-before-day">Before</span>
        <input
          type="date"
          value={value.before ?? ''}
          min={value.after ?? undefined}
          aria-labelledby="search-before-day"
          aria-label="Before day"
          aria-invalid={invalid || undefined}
          onChange={(e) => onChange({ ...value, before: e.target.value || null })}
          className={INPUT_CLASS}
        />
      </label>
      {(value.after || value.before) && (
        <button
          type="button"
          onClick={() => onChange(EMPTY_SEARCH_DAY_RANGE)}
          className="text-xs text-muted-foreground hover:text-foreground"
        >
          Clear dates
        </button>
      )}
      {invalid && (
        <span role="alert" className="text-xs text-destructive">
          “After” must be earlier than “Before”
        </span>
      )}
    </div>
  );
}
